import { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, limit, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useNavigate } from 'react-router-dom';

interface AnswerSheet {
    id: string;
    UserName: string;
    UserDisplayName?: string; // Added for display
    ShikakuName: string;
    TotalScore: number;
    CreateDate: any;
    UserRef?: any;
}

export default function AnswerSheetManagement() {
    const navigate = useNavigate();
    const [answers, setAnswers] = useState<AnswerSheet[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchAnswers = async () => {
        setLoading(true);
        try {
            // Fetch last 100 results
            const q = query(collection(db, 'AnswerSheet'), orderBy('CreateDate', 'desc'), limit(100));
            const querySnapshot = await getDocs(q);

            // Fetch user display names for each answer sheet
            const list = await Promise.all(
                querySnapshot.docs.map(async (docSnapshot) => {
                    const data = docSnapshot.data();
                    let userDisplayName = data.UserName || '不明';

                    // Try to fetch display name from UserRef
                    if (data.UserRef) {
                        try {
                            const userDoc = await getDoc(data.UserRef);
                            if (userDoc.exists()) {
                                userDisplayName = userDoc.data().display_name || data.UserName || '不明';
                            }
                        } catch (error) {
                            console.error('Error fetching user display name:', error);
                        }
                    }

                    return {
                        id: docSnapshot.id,
                        ...data,
                        UserDisplayName: userDisplayName
                    } as AnswerSheet;
                })
            );

            setAnswers(list);
        } catch (error) {
            console.error("Error fetching answers:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnswers();
    }, []);

    const handleDelete = async (id: string) => {
        if (!window.confirm('本当に削除しますか？')) return;
        try {
            await deleteDoc(doc(db, 'AnswerSheet', id));
            fetchAnswers();
        } catch (error) {
            console.error("Error deleting answer sheet:", error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-2xl font-bold text-gray-800">回答結果管理</h1>
                    <button onClick={() => navigate('/admin/dashboard')} className="text-gray-600 hover:text-gray-800 cursor-pointer">
                        ダッシュボードに戻る
                    </button>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    現在の表示件数: {answers.length} 件
                </div>

                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日時</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">資格名</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">得点</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={5} className="px-6 py-4 text-center">読み込み中...</td></tr>
                            ) : answers.map((answer) => (
                                <tr key={answer.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {answer.CreateDate?.toDate ? answer.CreateDate.toDate().toLocaleString('ja-JP') : '不明'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{answer.UserDisplayName || answer.UserName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{answer.ShikakuName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{answer.TotalScore}点</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => handleDelete(answer.id)} className="text-red-600 hover:text-red-900 cursor-pointer">削除</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
